<template>
  <ModalCard
    v-if="isOpen"
    title="Download Item Batch Creation Template"
    :handle-close="() => $emit('update:isOpen', false)"
  >
    <template #body-header>
      <div class="mb-4">
        <p class="mb-1">
          Download a template for batch creation of new
          {{ itemType }}.
        </p>
        <template v-if="customisable">
          <p class="mb-1">The template can be customised adding annotations.</p>
          <p>
            <b>We do advise annotating new items</b>
            . This will help later finding and identifying items.
          </p>
        </template>
      </div>
    </template>
    <template #body>
      <div>
        <div v-if="customisable && !customiseTemplate">
          <a @click="customiseTemplate = true">Customize template...</a>
        </div>
        <div v-if="customisable && customiseTemplate">
          <div
            class="title is-size-6 m-0 is-flex is-justify-content-space-between pb-2"
            style="border-bottom: 1px solid var(--color-border-default)"
          >
            Customise exported template
            <span
              class="icon hoverable-icon has-text-primary"
              @click="customiseTemplate = false"
            >
              <i class="fa-solid fa-toggle-on" />
            </span>
          </div>
          <div class="py-4">
            <div
              class="is-flex is-align-items-center is-justify-content-space-between mb-3"
            >
              <div class="title is-size-6 mb-0">Annotations</div>
              <div class="buttons has-addons">
                <button
                  class="button is-small"
                  :class="{
                    'is-primary is-selected': customiseAnnotations === false,
                  }"
                  @click="customiseAnnotations = false"
                >
                  All ({{ annotations.length }})
                </button>
                <button
                  class="button is-small"
                  :class="{
                    'is-primary is-selected': customiseAnnotations === true,
                  }"
                  @click="customiseAnnotations = true"
                >
                  Custom List ({{ exportedAnnotations.length }})
                </button>
              </div>
            </div>
            <p
              v-if="customisable && customiseTemplate && !customiseAnnotations"
              class="help mb-4"
            >
              <span class="icon has-text-warning">
                <i class="fa-solid fa-exclamation-triangle" />
              </span>
              Annotations are currently not filtered by model. By chosing
              <code class="has-text-primary">all</code>
              , a substancial number of column will be added to the template.
            </p>
            <div
              v-if="customisable && customiseAnnotations"
              class="is-flex is-align-items-center mb-2"
            >
              <SelectContainer
                v-model="exportedAnnotations"
                placeholder="Select annotations"
                label="label"
                track-by="value"
                :options="annotations"
                :multiple="true"
                :taggable="false"
                :close-on-select="false"
                style="width: 100%"
              />
              <button
                v-tooltip="'Reinitialised the list of relevant annotations'"
                class="button is-small is-primary is-outlined ml-3"
                @click="exportedAnnotations = relevantAnnotations"
              >
                <span class="icon">
                  <i class="fa-solid fa-undo" />
                </span>
              </button>
            </div>
            <div
              v-if="customisable && customiseTemplate"
              style="display: flex; margin: 1rem 0"
            >
              <div class="notification is-info">
                <span class="icon has-text-primary">
                  <i class="fa-solid fa-info-circle" />
                </span>
                <div class="text" style="font-size: 0.9em">
                  You can later delete columns in the spreadsheet if you decide
                  they are not relevant anymore. Empty columns are ignored
                  anyway.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div>
        <button
          class="button is-outlined is-small"
          @click="$emit('update:isOpen', false)"
        >
          <span>Cancel</span>
        </button>
        <BaseButton
          size="small"
          variant="primary"
          :outlined="true"
          :icon="['fa-solid', 'fa-download']"
          label="Download template"
          :loading="isDownloading"
          :disabled="isDownloading"
          @click="handleGetTemplate"
        />
      </div>
    </template>
  </ModalCard>
</template>

<script>
import { downloadFile } from '@/utils/api'
import SelectContainer from '../SelectContainer.vue'

export default {
  name: 'BatchCreateGetTemplateModal',
  emits: ['update:isOpen'],
  components: {
    SelectContainer,
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    itemType: {
      type: String,
    },
    baseUrl: {
      type: String,
    },
    customisable: {
      type: Boolean,
      required: false,
    },
    annotations: {
      type: Array,
      default: () => [],
    },
    relevantAnnotations: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      customiseTemplate: this.customisable,
      customiseAnnotations: true,
      exportedAnnotations: [],
      isDownloading: false,
    }
  },
  computed: {
    urlParams() {
      return this.customiseTemplate === true
        ? this.customiseAnnotations === true
          ? `annotation_fields=${this.exportedAnnotations
              .map((a) => a.value)
              .join(',')}`
          : 'annotation_fields=all'
        : ''
    },
    url() {
      return (
        this.baseUrl +
        ['format=xlsx', this.urlParams].filter((u) => u !== '').join('&')
      )
    },
  },
  watch: {
    isOpen(isOp) {
      if (isOp === true) {
        this.exportedAnnotations = this.relevantAnnotations
      } else if (isOp === false) {
        this.customiseTemplate = true
        this.customiseAnnotations = true
        this.exportedAnnotations = []
      }
    },
  },
  methods: {
    async handleGetTemplate() {
      this.isDownloading = true
      try {
        const response = await downloadFile({ url: this.url })
        if (response.status === 200) this.$emit('update:isOpen', false)
      } catch (e) {
        this.$store.commit('notify', {
          type: 'error',
          message: `Cannot retrieve template from server. Error: ${e}`,
        })
      } finally {
        this.isDownloading = false
      }
    },
  },
}
</script>
